import React, { FC, useCallback } from 'react';
import { IWorkspace } from 'src/models/workspace/types';
import { Modal, Space } from 'antd';
import { Button, notification } from 'src/common';
import { useTranslation } from 'react-i18next';
import { useDeleteWorkspace, useRestoreWorkspace, useActiveWsIdState } from 'src/models/workspace/hooks';
import { ProcurementsDisplay, OverviewQp } from 'src/models/procurements/types';
import { trackDeleteWorkspace, trackRestoreWorkspace } from 'src/segment/events';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import modalStyles from 'src/common/Modal/index.module.scss';
import { useSetOverviewQp } from 'src/models/procurements/hooks';

const { confirm } = Modal;

export interface IWorkspaceOperationsProps {
  workspace: IWorkspace;
  refetch: () => void;
}
export const WorkspaceOperations: FC<IWorkspaceOperationsProps> = props => {
  const { workspace, refetch } = props;
  const { t } = useTranslation();
  const [deleteWS] = useDeleteWorkspace();
  const setOverviewQp = useSetOverviewQp();
  const [, setActiveWsId] = useActiveWsIdState();
  const [[restoreWS, { loading: restoreInProgress }], { enabled: restoringAllowed }] = useRestoreWorkspace();

  const setTab = useCallback(
    (tab: ProcurementsDisplay) => {
      setOverviewQp({ [OverviewQp.Tab]: tab });
    },
    [setOverviewQp]
  );
  const setWorkspaceId = useCallback(
    (id: string) => {
      setActiveWsId(id);
    },
    [setActiveWsId]
  );
  const openWorkspace = useCallback(() => {
    setTab(ProcurementsDisplay.WORKSPACE);
    setWorkspaceId(workspace.id);
  }, [setTab, setWorkspaceId, workspace.id]);

  const onRestoreWorkspace = useCallback(() => {
    restoreWS({
      variables: {
        id: workspace.id
      }
    })
      .then(() => {
        trackRestoreWorkspace({
          id: workspace.id,
          name: workspace.name
        });
      })
      .catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
  }, [restoreWS, t, workspace.id, workspace.name]);

  const onDeleteWorkspace = useCallback(() => {
    confirm({
      title: t('BidSpaces.deleteBidSpace'),
      content: t('BidSpaces.deleteBidSpaceExplanation'),
      okText: t('Common.delete'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      onOk() {
        deleteWS({
          variables: {
            id: workspace.id
          }
        })
          .then(() => refetch())
          .then(() => {
            trackDeleteWorkspace({
              id: workspace.id
            });
          })
          .catch(() =>
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            })
          );
      },
      onCancel() {
        console.log('Cancel');
      }
    });
  }, [deleteWS, refetch, t, workspace.id]);
  return (
    <Space direction={'horizontal'}>
      <Button type={'primary'} onClick={openWorkspace}>
        {t('BidSpaces.view')}
      </Button>
      <Button type={'default'} onClick={onRestoreWorkspace} loading={restoreInProgress} disabled={!restoringAllowed}>
        {t('BidSpaces.restore')}
      </Button>
      <Button type={'default'} onClick={onDeleteWorkspace} icon={<FontAwesomeIcon icon={faTrash} />} />
    </Space>
  );
};
